import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery, Breakpoint } from '@mui/material';

// 设备类型枚举
export enum DeviceType {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

// 屏幕方向枚举
export enum Orientation {
  PORTRAIT = 'portrait',
  LANDSCAPE = 'landscape',
}

// 响应式状态接口
export interface ResponsiveState {
  deviceType: DeviceType;
  orientation: Orientation;
  breakpoint: Breakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isSmallScreen: boolean;
  isLargeScreen: boolean;
  screenWidth: number;
  screenHeight: number;
  isLandscape: boolean;
  isPortrait: boolean;
  isTouchDevice: boolean;
}

// 主要的响应式Hook
export const useResponsive = (): ResponsiveState => {
  const theme = useTheme();
  
  // MUI断点检测
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  const isLg = useMediaQuery(theme.breakpoints.only('lg'));
  const isXl = useMediaQuery(theme.breakpoints.only('xl'));
  
  // 设备类型检测
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  
  // 屏幕尺寸检测
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl'));
  
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });
  
  const [orientation, setOrientation] = useState<Orientation>(
    typeof window !== 'undefined' && window.innerWidth > window.innerHeight
      ? Orientation.LANDSCAPE
      : Orientation.PORTRAIT
  );
  
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      
      setWindowSize({
        width: newWidth,
        height: newHeight,
      });
      
      setOrientation(
        newWidth > newHeight ? Orientation.LANDSCAPE : Orientation.PORTRAIT
      );
    };

    // 检测触摸设备
    const checkTouchDevice = () => {
      const hasTouch = 'ontouchstart' in window || 
                      navigator.maxTouchPoints > 0 || 
                      (navigator as any).msMaxTouchPoints > 0;
      setIsTouchDevice(hasTouch);
    };

    if (typeof window !== 'undefined') {
      checkTouchDevice();
      window.addEventListener('resize', handleResize);
      window.addEventListener('orientationchange', handleResize);
      
      // 处理方向变化的延迟
      const handleOrientationChange = () => {
        setTimeout(handleResize, 100);
      };
      window.addEventListener('orientationchange', handleOrientationChange);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('orientationchange', handleResize);
        window.removeEventListener('orientationchange', handleOrientationChange);
      };
    }
  }, []);

  // 确定当前断点
  const getCurrentBreakpoint = (): Breakpoint => {
    if (isXs) return 'xs';
    if (isSm) return 'sm';
    if (isMd) return 'md';
    if (isLg) return 'lg';
    if (isXl) return 'xl';
    return 'md'; // 默认值
  };

  // 确定设备类型
  const getDeviceType = (): DeviceType => {
    if (isMobile) return DeviceType.MOBILE;
    if (isTablet) return DeviceType.TABLET;
    return DeviceType.DESKTOP;
  };

  return {
    deviceType: getDeviceType(),
    orientation,
    breakpoint: getCurrentBreakpoint(),
    isMobile,
    isTablet,
    isDesktop,
    isSmallScreen,
    isLargeScreen,
    screenWidth: windowSize.width,
    screenHeight: windowSize.height,
    isLandscape: orientation === Orientation.LANDSCAPE,
    isPortrait: orientation === Orientation.PORTRAIT,
    isTouchDevice,
  };
};

// 断点检测Hook
export const useBreakpoint = () => {
  const theme = useTheme();
  
  return {
    isXs: useMediaQuery(theme.breakpoints.only('xs')),
    isSm: useMediaQuery(theme.breakpoints.only('sm')),
    isMd: useMediaQuery(theme.breakpoints.only('md')),
    isLg: useMediaQuery(theme.breakpoints.only('lg')),
    isXl: useMediaQuery(theme.breakpoints.only('xl')),
    isUpSm: useMediaQuery(theme.breakpoints.up('sm')),
    isUpMd: useMediaQuery(theme.breakpoints.up('md')),
    isUpLg: useMediaQuery(theme.breakpoints.up('lg')),
    isUpXl: useMediaQuery(theme.breakpoints.up('xl')),
    isDownSm: useMediaQuery(theme.breakpoints.down('sm')),
    isDownMd: useMediaQuery(theme.breakpoints.down('md')),
    isDownLg: useMediaQuery(theme.breakpoints.down('lg')),
    isDownXl: useMediaQuery(theme.breakpoints.down('xl')),
  };
};

// 设备方向Hook
export const useOrientation = () => {
  const [orientation, setOrientation] = useState<Orientation>(
    typeof window !== 'undefined' && window.innerWidth > window.innerHeight
      ? Orientation.LANDSCAPE
      : Orientation.PORTRAIT
  );

  useEffect(() => {
    const handleOrientationChange = () => {
      // 添加延迟以确保获取正确的尺寸
      setTimeout(() => {
        setOrientation(
          window.innerWidth > window.innerHeight 
            ? Orientation.LANDSCAPE 
            : Orientation.PORTRAIT
        );
      }, 100);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('orientationchange', handleOrientationChange);
      window.addEventListener('resize', handleOrientationChange);
      
      return () => {
        window.removeEventListener('orientationchange', handleOrientationChange);
        window.removeEventListener('resize', handleOrientationChange);
      };
    }
  }, []);

  return {
    orientation,
    isLandscape: orientation === Orientation.LANDSCAPE,
    isPortrait: orientation === Orientation.PORTRAIT,
  };
};

// 触摸设备检测Hook
export const useTouchDevice = () => {
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const checkTouchDevice = () => {
      const hasTouch = 'ontouchstart' in window || 
                      navigator.maxTouchPoints > 0 || 
                      (navigator as any).msMaxTouchPoints > 0;
      setIsTouchDevice(hasTouch);
    };

    if (typeof window !== 'undefined') {
      checkTouchDevice();
    }
  }, []);

  return isTouchDevice;
};

// 视口尺寸Hook
export const useViewportSize = () => {
  const [size, setSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  return size;
}; 